'use client';

import { Eye, EyeOff } from 'lucide-react';
import { type ChangeEvent, useState } from 'react';

/**
 * 2026-07-07 (Leon feedback) — password input with a show/hide toggle.
 *
 * Wraps a native <input> and stacks an eye button in the right padding.
 * The toggle only flips the input's `type` in local state; it never
 * pings the server, doesn't autofocus, and doesn't survive unmount.
 *
 * Padding-right is 44px (11 × 4px) so the icon has room. Callers pass
 * the same className they'd give a bare input; we merge in the
 * `pr-11` to reserve icon space without breaking existing tailwind
 * variants (bg color, border, ring, etc).
 *
 * Used on:
 *   - /studio/settings/security  (all 3 fields)
 *   - /login/password            (login)
 *   - /login/reset               (both new-password fields)
 *
 * Intentionally NOT used on /platform (admin sets password on behalf of
 * a user) — that field is already type="text" so the admin can copy the
 * literal string to send over Slack/WeChat.
 */
export function PasswordField({
  id,
  value,
  onChange,
  autoComplete,
  placeholder,
  className,
  ariaLabelToggle,
}: {
  id?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  autoComplete?: 'current-password' | 'new-password' | 'off';
  placeholder?: string;
  /**
   * Full className for the input — same as the plain <input> variant.
   * `pr-11` is appended so the toggle button doesn't overlap text.
   */
  className: string;
  /** For the eye button. Defaults to a Chinese label. */
  ariaLabelToggle?: string;
}) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  const label = ariaLabelToggle ?? (visible ? '隐藏密码' : '显示密码');
  return (
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className={`${className} pr-11`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={label}
        title={label}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-mute hover:text-ink transition p-1 bg-transparent border-0 cursor-pointer"
      >
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </button>
    </div>
  );
}
